import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  clearNarrativeDraft,
  clearNarrativeFlow,
  deleteNarrativeDraft,
  favoriteProject,
  hydrateDynamicNarrativeFlowState,
  readProject,
  renameProject,
  updateFlowState,
  updateFlowStateTopicIds,
  updateNarrativeDraft,
} from '@api/narrative-projects';
import { mapFlowStateTopicsToDrafts } from '@api/narrative-projects/utilities/helpers';
import {
  createNarrative,
  deleteNarratives,
  readDraftTopics,
  reorderFlowTopics,
  softDeleteFlowTopic,
} from '@api/narratives';
import { normalizeTopicIds } from '@helpers';
import { EditableTitle, FavoriteToggle, toast } from '@utilities';
import { flowTopicTag, narrativeErrorStatuses } from '@helpers/narratives';
import Footer from '../../../../../../components/Footer';
import NarrativeFlowView from '../../../../components/NarrativeFlowView';
import NarrativeTitle from '../../../../components/NarrativeFlowView/components/NarrativeTitle';
import NarrativeTopicCard from '../../../../components/NarrativeFlowView/components/NarrativeTopicCard';
import PreBuildModal from '../../../../components/PreBuildModal';
import ModalBuildNarrative from '../../../Narratives/components/ModalBuildNarrative';
import {
  getPromptPillTooltip,
  getPromptPillValue,
  transformPromptSelections,
} from '../../../Narratives/utilities/helpers';
import ConfirmActionModal from '../ConfirmActionModal';

import { DEFAULT_DRAFT_NARRATIVE_NAME } from '../../utilities/constants';
import { mergeDraftIntoFlowState } from '../../utilities/helpers';

import {
  areRequiredPromptsFilled,
  buildDraftFlowData,
  buildDraftTopicData,
  buildPromptsFromFlowState,
  buildTopicConfigurationLookup,
  buildTopicShell,
  checkIsTopicCardCountMax,
  EMPTY_DOWNLOAD_MAP,
  extractEditablePromptAnswers,
  findFirstAvailableVariantSelection,
  getVariantSignature,
  hasAvailableAlternativeVariant,
  hasPromptRequirements,
  trackTopicCardAddRemove,
} from '../../utilities/helpers';

import CanvasFloatingToolbar from './components/CanvasFloatingToolbar';
import ConfigureModal from './components/ConfigureModal';
import RunFlowTopicsObserver from './components/RunFlowTopicsObserver';
import {
  COMPLETED_OR_ERROR_FLOW_STATUSES,
  CONFIGURABLE_CARD_DISABLED_TOOLTIP,
  DEFAULT_PROJECT_NAME,
  DEFAULT_PROMPT_ROUTE_LABEL,
  PROJECT_RENAME_DEBOUNCE_MS,
  TOPIC_CARD_MOTION_PROPS,
} from './utilities/constants';
import {
  buildEnrichedDraftTopics,
  buildManualSelectedDrafts,
  buildPromptAnswersPayload,
  extractComponentIds,
  getRunComponentIds,
  mergeDraftTopics,
  mergeUniqueValues,
  pruneDraftDatasetMap,
} from './utilities/helpers';

import styles from './_index.module.scss';

const hasMultipleConfigVariants = (configMeta = null) => {
  if (!configMeta || typeof configMeta !== 'object') return false;
  if (Number(configMeta?.totalVariants) > 1) return true;

  const variantIds = Object.values(configMeta?.variantMap || {})
    .map((variant) => variant?.id)
    .filter((id) => id != null);

  return new Set(variantIds.map(String)).size > 1;
};

const isDraftContextUnchanged = (latestContext, requestContext) => {
  if (latestContext.chatSessionId !== requestContext.chatSessionId) return false;
  if (latestContext.flowId !== requestContext.flowId) return false;
  if (latestContext.draftIdentitySignature !== requestContext.draftIdentitySignature) return false;
  if (requestContext.requireMatchingDraftSignature) {
    if (latestContext.draftSignature !== requestContext.draftSignature) return false;
  }
  if (!requestContext.wasViewingDraft || !latestContext.isViewingDraft) return false;
  return true;
};

const buildOccupiedTopicIdSet = (topicIds = []) => new Set(normalizeTopicIds(topicIds).map(String));

const shouldShowRunTopicCard = (topic) => {
  if (!topic?.isHidden) return true;

  const tags = topic?.tags || {};
  const isNoSample = flowTopicTag?.NO_SAMPLE in tags;
  const isInternalError = narrativeErrorStatuses.includes(topic?.status);
  return isNoSample || isInternalError;
};

const NarrativesCanvas = ({
  activeNarrativeName = null,
  activeTopicConfiguration = null,
  chatSessionId,
  externalIsFavorite = null,
  externalProjectName = null,
  flowId,
  flowState,
  hasProjectDraft,
  isProjectDraftLookupPending = false,
  isRunTopicMutationInProgress = false,
  onActiveTopicConfigurationChange,
  onDraftCleared,
  onDraftConsumed,
  onDraftSaved,
  onEnsureChatSession,
  onFavoriteChange,
  onFlowSelected,
  onFlowStateChange,
  onInsertRunTopicConfiguration,
  onNarrativeCreated,
  onNarrativeDeleted,
  onNarrativeNameChange,
  onProjectNameChange,
  onProjectNotFound,
  onReplaceRunTopicConfiguration,
  onRequestTopicConfigurationLookup,
  onRunFlowTopicIdsChange,
  onSelectedTopicsChange,
  onTemplateSaved,
  runFlowRefreshKey = 0,
  selectedTopicCards = [],
  topicConfigurationLookup = {},
}) => {
  const [activePrompt, setActivePrompt] = useState(null);
  const [activeConfigPreviewErrorTopicId, setActiveConfigPreviewErrorTopicId] = useState(null);
  const [buildError, setBuildError] = useState(null);
  const [draftDatasetMap, setDraftDatasetMap] = useState({});
  const [isBuilding, setIsBuilding] = useState(false);
  const [pendingBuildFlowId, setPendingBuildFlowId] = useState(null);
  const [pendingDraftAction, setPendingDraftAction] = useState(null);
  const [pendingRunTopicAction, setPendingRunTopicAction] = useState(null);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [project, setProject] = useState(null);
  const [promptModalName, setPromptModalName] = useState(
    flowState?.narrativeName?.trim() || DEFAULT_DRAFT_NARRATIVE_NAME
  );
  const [selectedRunPrompt, setSelectedRunPrompt] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [transformedAnswersMap, setTransformedAnswersMap] = useState({});
  const [topicPendingDeletion, setTopicPendingDeletion] = useState(null);
  const [draftPendingDeletion, setDraftPendingDeletion] = useState(null);
  const [isClearingCanvas, setIsClearingCanvas] = useState(false);
  const previousProjectNameRef = useRef(DEFAULT_PROJECT_NAME);
  const isExternalProjectNameUpdateRef = useRef(false);
  const latestExternalProjectNameRef = useRef(externalProjectName);
  const latestExternalIsFavoriteRef = useRef(externalIsFavorite);
  const latestOnProjectNotFoundRef = useRef(onProjectNotFound);
  const promptSelectionsSnapshotRef = useRef(null);
  const shouldPersistPromptChangesRef = useRef(false);
  const shouldPersistDraftCardChangesRef = useRef(false);
  const lastPersistedDraftSignatureRef = useRef(null);
  const draftPersistenceRequestIdRef = useRef(0);
  const topicOrderPersistenceRequestIdRef = useRef(0);
  const hasUserEditedDraftNameRef = useRef(false);
  const latestDraftPersistContextRef = useRef({
    chatSessionId: null,
    draftIdentitySignature: null,
    draftSignature: null,
    flowId: null,
    isViewingDraft: false,
  });
  const projectReadRequestIdRef = useRef(0);
  const renameProjectRequestIdRef = useRef(0);
  const latestRequestedProjectNameRef = useRef('');
  const prompts = useMemo(() => buildPromptsFromFlowState(flowState), [flowState]);
  const visiblePrompts = useMemo(() => prompts.filter((prompt) => !prompt?.isAdvanced), [prompts]);
  const allVisiblePrompts = useMemo(
    () => visiblePrompts.flatMap((prompt) => [prompt, ...(prompt?.groupedPrompts || [])]),
    [visiblePrompts]
  );
  const canSelectPrompts = hasPromptRequirements(flowState) && visiblePrompts.length > 0;
  const isViewingRun = Boolean(flowId);
  const isViewingDraft = Boolean(flowState) && !isViewingRun;

  const narrativeFlow = useSelector((state) => {
    if (!flowId) return null;

    const flows = state?.websocket?.flows;
    if (!Array.isArray(flows)) return null;

    return flows.find((flow) => Number(flow?.id) === Number(flowId)) || null;
  });
  const userPermissions = useSelector((state) => state?.user?.data?.permissions) || {};
  const canManageRunTopics =
    Boolean(userPermissions?.canAccessBuildYourOwn) || Boolean(userPermissions?.canAccessChat);

  const flowStateDraftTopics = useMemo(
    () =>
      (flowState ? mapFlowStateTopicsToDrafts(flowState) : []).map((draft) => {
        const topicConfiguration = topicConfigurationLookup?.[String(draft?.componentId)];
        if (!topicConfiguration?.config)
          return {
            ...draft,
            name: topicConfiguration?.name ?? draft?.name ?? null,
          };
        return {
          ...draft,
          configMeta: topicConfiguration.config,
          name: topicConfiguration?.name ?? draft?.name ?? null,
          selectedVariant: topicConfiguration.selectedVariant,
          variantSignature: getVariantSignature(
            topicConfiguration.selectedVariant,
            topicConfiguration.config.signatureDimensions
          ),
        };
      }),
    [flowState, topicConfigurationLookup]
  );
  const manualSelectedDrafts = useMemo(
    () => buildManualSelectedDrafts(selectedTopicCards),
    [selectedTopicCards]
  );
  const manualTopicOrder = useMemo(
    () => manualSelectedDrafts.map((draft) => draft.componentId),
    [manualSelectedDrafts]
  );
  const manualCardRefs = useRef(new Map());
  const manualCardRefCallbacks = useRef(new Map());
  const draftScrollContainerRef = useRef(null);
  const manualOrderChangeSourceRef = useRef(false);
  const previousManualCountRef = useRef(manualTopicOrder.length);
  const previousManualOrderRef = useRef(manualTopicOrder);

  const draftTopics = useMemo(
    () => mergeDraftTopics(flowStateDraftTopics, manualSelectedDrafts),
    [flowStateDraftTopics, manualSelectedDrafts]
  );
  const activeConfigCard = activeTopicConfiguration;
  const hasDraftTopicCards = draftTopics.length > 0;
  const activeConfigVariantSignature = getVariantSignature(
    activeConfigCard?.selectedVariant,
    activeConfigCard?.config?.signatureDimensions
  );
  const activeConfigTopic = activeConfigCard?.config?.variantMap?.[activeConfigVariantSignature];
  const activeConfigTopicId = activeConfigTopic?.id;

  const componentIdsRef = useRef([]);
  const componentIds = useMemo(() => {
    const ids = extractComponentIds(draftTopics);
    const previous = componentIdsRef.current;
    const isSameLength = previous.length === ids.length;
    const isSameOrder = isSameLength && previous.every((id, index) => id === ids[index]);

    if (isSameOrder) {
      return previous;
    }

    componentIdsRef.current = ids;
    return ids;
  }, [draftTopics]);

  const draftFlowData = useMemo(
    () => buildDraftFlowData(flowState, componentIds),
    [flowState, componentIds]
  );

  const flowStateTopicIds = useMemo(() => {
    if (!Array.isArray(flowState?.topics)) return [];
    return flowState.topics.filter(
      (componentId) => componentId !== undefined && componentId !== null
    );
  }, [flowState]);
  const occupiedTopicIdSet = useMemo(() => {
    const manualTopicIds = (selectedTopicCards || [])
      .filter((card) => card?.id != null)
      .map((card) => card.id);
    return buildOccupiedTopicIdSet([...flowStateTopicIds, ...manualTopicIds]);
  }, [flowStateTopicIds, selectedTopicCards]);
  const activeConfigOccupiedTopicIdSet = useMemo(() => {
    if (!Array.isArray(activeConfigCard?.occupiedTopicIds)) {
      return occupiedTopicIdSet;
    }

    return buildOccupiedTopicIdSet(activeConfigCard.occupiedTopicIds);
  }, [activeConfigCard?.occupiedTopicIds, occupiedTopicIdSet]);
  const draftFlowStateId = flowState ? (flowState['flowId'] ?? null) : null;

  const draftIdentitySignature = useMemo(
    () =>
      JSON.stringify({
        chatSessionId: chatSessionId || null,
        flowStateId: draftFlowStateId,
        focusFlowId: flowState?.focusFlowId || null,
        narrativeId: flowState?.narrativeId || null,
      }),
    [chatSessionId, draftFlowStateId, flowState?.focusFlowId, flowState?.narrativeId]
  );

  const draftEditablePromptAnswers = useMemo(() => {
    if (!isPromptModalOpen) {
      return flowState?.editablePromptAnswers || {};
    }

    const nextPromptAnswers = {
      ...(flowState?.editablePromptAnswers || {}),
    };

    allVisiblePrompts.forEach((prompt) => {
      if (!Object.prototype.hasOwnProperty.call(selectedAnswers, prompt.promptKey)) return;
      nextPromptAnswers[prompt.promptKey] = selectedAnswers[prompt.promptKey];
    });

    return nextPromptAnswers;
  }, [allVisiblePrompts, flowState?.editablePromptAnswers, isPromptModalOpen, selectedAnswers]);

  const draftNarrativeName = useMemo(() => {
    return promptModalName?.trim() || DEFAULT_DRAFT_NARRATIVE_NAME;
  }, [promptModalName]);
  const topicOrderFocusFlowId = flowId ?? null;
  const flowStateNarrativeName = useMemo(
    () => flowState?.narrativeName?.trim() || DEFAULT_DRAFT_NARRATIVE_NAME,
    [flowState?.narrativeName]
  );
  const isDraftTitleSyncedToFlowState = draftNarrativeName === flowStateNarrativeName;

  useEffect(() => {
    if (hasUserEditedDraftNameRef.current) return;
    if (isPromptModalOpen) return;

    const nextNarrativeName = flowStateNarrativeName;

    setPromptModalName((currentName) => {
      const currentNarrativeName = currentName?.trim() || DEFAULT_DRAFT_NARRATIVE_NAME;
      return currentNarrativeName === nextNarrativeName ? currentName : nextNarrativeName;
    });
  }, [flowStateNarrativeName, isPromptModalOpen]);

  useEffect(() => {
    hasUserEditedDraftNameRef.current = false;
    lastPersistedDraftSignatureRef.current = null;
    draftPersistenceRequestIdRef.current += 1;
    shouldPersistPromptChangesRef.current = false;
    shouldPersistDraftCardChangesRef.current = false;
    promptSelectionsSnapshotRef.current = null;
    setPromptModalName(flowStateNarrativeName);
  }, [draftIdentitySignature]);

  const draftCanvasFlowState = useMemo(() => {
    if (!isViewingDraft || !flowState) return null;

    return {
      ...flowState,
      editablePromptAnswers: draftEditablePromptAnswers,
      narrativeName: draftNarrativeName,
      topics: componentIds,
    };
  }, [componentIds, draftEditablePromptAnswers, draftNarrativeName, flowState, isViewingDraft]);

  const draftCanvasFlowStateSignature = useMemo(() => {
    if (!draftCanvasFlowState) return null;

    return JSON.stringify({
      editablePromptAnswers: draftCanvasFlowState.editablePromptAnswers || {},
      narrativeName: draftCanvasFlowState.narrativeName || '',
      topics: draftCanvasFlowState.topics || [],
    });
  }, [draftCanvasFlowState]);

  useEffect(() => {
    latestDraftPersistContextRef.current = {
      chatSessionId,
      draftIdentitySignature,
      draftSignature: draftCanvasFlowStateSignature,
      flowId,
      isViewingDraft,
    };
  }, [
    chatSessionId,
    draftCanvasFlowStateSignature,
    draftIdentitySignature,
    flowId,
    isViewingDraft,
  ]);

  useEffect(() => {
    topicOrderPersistenceRequestIdRef.current += 1;
  }, [chatSessionId, draftIdentitySignature, flowId, isViewingDraft]);

  const currentDraftFlowStateSignature = useMemo(() => {
    if (!flowState) return null;

    return JSON.stringify({
      editablePromptAnswers: flowState?.editablePromptAnswers || {},
      narrativeName: flowState?.narrativeName || '',
      topics: flowState?.topics || [],
    });
  }, [flowState]);

  const enrichedDraftTopics = useMemo(
    () => buildEnrichedDraftTopics(draftTopics, draftDatasetMap),
    [draftDatasetMap, draftTopics]
  );

  const canPersistDraftOrder = Boolean(chatSessionId);
  const canManageManualDrafts = typeof onSelectedTopicsChange === 'function';
  const isDraftActionPending = Boolean(pendingDraftAction);
  const normalizedFlowStateComponentIds = useMemo(
    () => normalizeTopicIds(flowState?.topics),
    [flowState?.topics]
  );

  const isBuildDisabled = !areRequiredPromptsFilled(visiblePrompts, transformedAnswersMap);
  const hasAllRequiredPrompts = !isBuildDisabled;
  const hasPromptSelections = useMemo(() => {
    const editableAnswers = flowState?.editablePromptAnswers || {};
    return Object.values(editableAnswers).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object') return Object.keys(value).length > 0;
      return Boolean(value);
    });
  }, [flowState?.editablePromptAnswers]);
  const needsPromptSelection = isViewingDraft && !hasAllRequiredPrompts;
  const canCreateNarrative = isViewingDraft && hasAllRequiredPrompts && hasPromptSelections;
  const isNarrativeProcessing =
    Boolean(flowId) &&
    Boolean(narrativeFlow?.status) &&
    !COMPLETED_OR_ERROR_FLOW_STATUSES.includes(narrativeFlow.status);
  const hasPendingBuildFlow =
    pendingBuildFlowId != null && flowId != null && String(pendingBuildFlowId) === String(flowId);
  const isPendingBuildFlow = hasPendingBuildFlow && !narrativeFlow;

  const isToolbarActionLoading = Boolean(isNarrativeProcessing || isBuilding || isPendingBuildFlow);
  let toolbarActionLabel = 'Select Prompts';
  if (isViewingRun) {
    toolbarActionLabel = 'Reprompt';
  } else if (canCreateNarrative) {
    toolbarActionLabel = 'Create Narrative';
  }

  useEffect(() => {
    if (!hasPendingBuildFlow || !narrativeFlow) return;

    setPendingBuildFlowId(null);
  }, [hasPendingBuildFlow, narrativeFlow]);

  const promptsUnavailable = !canSelectPrompts && needsPromptSelection;
  const isToolbarActionDisabled = promptsUnavailable || isToolbarActionLoading;
  const shouldShowToolbar = isViewingRun || (isViewingDraft && hasDraftTopicCards);

  useEffect(() => {
    latestExternalProjectNameRef.current = externalProjectName;
    latestExternalIsFavoriteRef.current = externalIsFavorite;
  }, [externalIsFavorite, externalProjectName]);

  useEffect(() => {
    latestOnProjectNotFoundRef.current = onProjectNotFound;
  }, [onProjectNotFound]);

  useEffect(() => {
    if (!chatSessionId) {
      setProject(null);
      previousProjectNameRef.current = DEFAULT_PROJECT_NAME;
      return;
    }

    const controller = new AbortController();
    const requestId = ++projectReadRequestIdRef.current;
    setProject(null);
    previousProjectNameRef.current = DEFAULT_PROJECT_NAME;

    readProject({ chatSessionId, signal: controller.signal })
      .then((response) => {
        if (requestId !== projectReadRequestIdRef.current) return;
        if (response) {
          // Read the latest external values (not the closure snapshot) so a sync that
          // arrives while this fetch is in flight can't be lost to stale backend data.
          const latestExternalName = latestExternalProjectNameRef.current;
          const latestExternalFavorite = latestExternalIsFavoriteRef.current;
          const syncedName = typeof latestExternalName === 'string' ? latestExternalName : null;
          const resolvedName =
            syncedName ??
            (typeof response?.name === 'string' && response.name.trim() ? response.name : null);
          const resolvedIsFavorite =
            typeof latestExternalFavorite === 'boolean'
              ? latestExternalFavorite
              : Boolean(response?.isFavorite);
          setProject({
            ...response,
            isFavorite: resolvedIsFavorite,
            name: resolvedName || DEFAULT_PROJECT_NAME,
          });
          previousProjectNameRef.current = resolvedName || DEFAULT_PROJECT_NAME;
          if (typeof onProjectNameChange === 'function') {
            onProjectNameChange(chatSessionId, resolvedName || DEFAULT_PROJECT_NAME);
          }
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;
        if (requestId !== projectReadRequestIdRef.current) return;
        if (
          error?.response?.status === 404 &&
          typeof latestOnProjectNotFoundRef.current === 'function'
        ) {
          const didHandleNotFound = latestOnProjectNotFoundRef.current(chatSessionId);
          if (didHandleNotFound) return;
        }
        console.error('Failed to fetch project name', error);
      });

    return () => {
      controller.abort();
    };
  }, [chatSessionId]);

  useEffect(() => {
    if (!chatSessionId || typeof externalProjectName !== 'string') return;

    setProject((previousProject) => {
      if (!previousProject) return previousProject;
      if (previousProject.name === externalProjectName) return previousProject;
      isExternalProjectNameUpdateRef.current = true;
      return { ...previousProject, name: externalProjectName };
    });
  }, [chatSessionId, externalProjectName]);

  useEffect(() => {
    if (!chatSessionId || typeof externalIsFavorite !== 'boolean') return;

    setProject((previousProject) => {
      if (!previousProject) return previousProject;
      if (Boolean(previousProject.isFavorite) === externalIsFavorite) return previousProject;
      return { ...previousProject, isFavorite: externalIsFavorite };
    });
  }, [chatSessionId, externalIsFavorite]);

  const persistTopicOrder = useCallback(
    async (nextIds) => {
      if (!chatSessionId) return null;

      const requestId = ++topicOrderPersistenceRequestIdRef.current;

      try {
        const isResettingDraft = nextIds.length === 0;
        let nextFlowState = flowState
          ? {
              ...flowState,
              ...(isResettingDraft
                ? {
                    editablePromptAnswers: {},
                    focusFlowId: null,
                    narrativeName: DEFAULT_DRAFT_NARRATIVE_NAME,
                  }
                : {}),
              topics: nextIds,
              ...(topicOrderFocusFlowId != null ? { focusFlowId: topicOrderFocusFlowId } : {}),
            }
          : {
              ...(isResettingDraft
                ? {
                    editablePromptAnswers: {},
                    focusFlowId: null,
                    narrativeName: DEFAULT_DRAFT_NARRATIVE_NAME,
                  }
                : {}),
              topics: nextIds,
              ...(topicOrderFocusFlowId != null ? { focusFlowId: topicOrderFocusFlowId } : {}),
            };

        if (topicOrderFocusFlowId == null) {
          const projectId = project?.id ?? (await readProject({ chatSessionId }))?.id;
          if (!projectId) throw new Error('Project ID is required to update a Draft.');

          if (isResettingDraft) {
            await clearNarrativeDraft({ projectId });
          } else {
            const updatedDraft = await updateNarrativeDraft({
              draft: nextFlowState,
              projectId,
            });
            nextFlowState = await hydrateDynamicNarrativeFlowState({
              flowState: mergeDraftIntoFlowState({ draft: updatedDraft, flowState: nextFlowState }),
            });
          }
          onDraftSaved?.({ chatSessionId });
        } else {
          await updateFlowStateTopicIds({
            chatSessionId,
            topicIds: nextIds,
          });
        }

        const updatedFlowState = await hydrateDynamicNarrativeFlowState({
          flowState: nextFlowState,
        });

        if (requestId !== topicOrderPersistenceRequestIdRef.current) return null;

        if (onFlowStateChange) {
          onFlowStateChange(updatedFlowState);
        }

        return updatedFlowState;
      } catch (error) {
        console.error('Failed to update topic order', error);
        toast.error('We could not update your topic order. Please try again.');
        throw error;
      }
    },
    [chatSessionId, flowState, onDraftSaved, onFlowStateChange, project?.id, topicOrderFocusFlowId]
  );

  useEffect(() => {
    if (!flowState || !visiblePrompts.length || isPromptModalOpen) return;

    const hydratedAnswers = extractEditablePromptAnswers(flowState);

    setSelectedAnswers(hydratedAnswers);

    const initialTransformedAnswers = allVisiblePrompts.reduce((acc, prompt) => {
      const answers = hydratedAnswers?.[prompt.promptKey];
      const hasAnswers = Array.isArray(answers) ? answers.length > 0 : Boolean(answers);

      if (!hasAnswers) return acc;

      try {
        acc[prompt.promptKey] = transformPromptSelections({ answers, prompt });
      } catch (error) {
        console.error('Failed to transform prompt answers from chat flow state:', error);
      }

      return acc;
    }, {});

    setTransformedAnswersMap(initialTransformedAnswers);

    setActivePrompt((currentPrompt) => {
      if (
        currentPrompt &&
        visiblePrompts.some((prompt) => prompt.promptKey === currentPrompt.promptKey)
      ) {
        return currentPrompt;
      }

      return visiblePrompts[0] || null;
    });
  }, [allVisiblePrompts, flowState, isPromptModalOpen, visiblePrompts]);

  useEffect(() => {
    if (!draftCanvasFlowState || !onFlowStateChange) return;
    if (isPromptModalOpen) return;
    if (!hasUserEditedDraftNameRef.current && !isDraftTitleSyncedToFlowState) return;
    if (draftCanvasFlowStateSignature === currentDraftFlowStateSignature) {
      if (shouldPersistDraftCardChangesRef.current) {
        return;
      }
      if (
        !hasUserEditedDraftNameRef.current &&
        lastPersistedDraftSignatureRef.current !== draftCanvasFlowStateSignature
      ) {
        lastPersistedDraftSignatureRef.current = draftCanvasFlowStateSignature;
      }
      return;
    }

    onFlowStateChange(draftCanvasFlowState);
  }, [
    currentDraftFlowStateSignature,
    draftCanvasFlowState,
    draftCanvasFlowStateSignature,
    isDraftTitleSyncedToFlowState,
    isPromptModalOpen,
    onFlowStateChange,
  ]);

  useEffect(() => {
    if (!chatSessionId || !draftCanvasFlowState || !draftCanvasFlowStateSignature) return;
    if (isPromptModalOpen) return;
    if (!hasUserEditedDraftNameRef.current && !isDraftTitleSyncedToFlowState) return;
    if (draftCanvasFlowStateSignature === lastPersistedDraftSignatureRef.current) return;

    const requestId = ++draftPersistenceRequestIdRef.current;
    const requestContext = {
      chatSessionId,
      draftIdentitySignature,
      draftSignature: draftCanvasFlowStateSignature,
      flowId,
      requireMatchingDraftSignature: true,
      wasViewingDraft: isViewingDraft,
    };
    const requestSignature = draftCanvasFlowStateSignature;
    const timeoutId = window.setTimeout(async () => {
      try {
        // The project read can fail independently of the session, so fall back to resolving the
        // project here rather than silently skipping the save.
        const projectId = project?.id ?? (await readProject({ chatSessionId }))?.id;
        if (!projectId) throw new Error('Project ID is required to update a Draft.');

        const updatedDraft = await updateNarrativeDraft({
          draft: draftCanvasFlowState,
          projectId,
        });
        const updatedFlowState = await hydrateDynamicNarrativeFlowState({
          flowState: mergeDraftIntoFlowState({
            draft: updatedDraft,
            flowState: draftCanvasFlowState,
          }),
        });

        if (requestId !== draftPersistenceRequestIdRef.current) return;
        const latestContext = latestDraftPersistContextRef.current;
        // Guard against out-of-order responses after session/mode/draft changes.
        if (!isDraftContextUnchanged(latestContext, requestContext)) return;

        lastPersistedDraftSignatureRef.current = requestSignature;
        shouldPersistDraftCardChangesRef.current = false;
        onDraftSaved?.({ chatSessionId });

        if (updatedFlowState && onFlowStateChange) {
          onFlowStateChange(updatedFlowState);
        }
      } catch (error) {
        if (requestId !== draftPersistenceRequestIdRef.current) return;
        if (!isDraftContextUnchanged(latestDraftPersistContextRef.current, requestContext)) return;
        console.error('Failed to persist draft flow state', error);
        toast.error('We could not save your Draft. Please try again.');
      }
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
      if (draftPersistenceRequestIdRef.current === requestId) {
        draftPersistenceRequestIdRef.current += 1;
      }
    };
  }, [
    chatSessionId,
    draftCanvasFlowState,
    draftCanvasFlowStateSignature,
    draftIdentitySignature,
    flowId,
    isDraftTitleSyncedToFlowState,
    isViewingDraft,
    isPromptModalOpen,
    onFlowStateChange,
    onDraftSaved,
    project?.id,
  ]);

  useEffect(() => {
    setDraftDatasetMap((prev) => pruneDraftDatasetMap(prev, componentIds));
  }, [componentIds]);

  useEffect(() => {
    const previousOrder = previousManualOrderRef.current || [];
    const orderChanged =
      previousOrder.length !== manualTopicOrder.length ||
      previousOrder.some((id, index) => id !== manualTopicOrder[index]);

    if (!orderChanged) return;

    if (!canPersistDraftOrder || !canManageManualDrafts) {
      manualOrderChangeSourceRef.current = false;
      return;
    }

    previousManualOrderRef.current = manualTopicOrder;

    if (manualOrderChangeSourceRef.current) {
      manualOrderChangeSourceRef.current = false;
      return;
    }

    const combinedOrder = mergeUniqueValues(flowStateTopicIds, manualTopicOrder);
    if (!combinedOrder.length) {
      manualOrderChangeSourceRef.current = false;
      return;
    }
    persistTopicOrder(combinedOrder).catch(() => {});
  }, [
    canManageManualDrafts,
    canPersistDraftOrder,
    flowStateTopicIds,
    manualTopicOrder,
    persistTopicOrder,
  ]);

  useEffect(() => {
    const previousCount = previousManualCountRef.current;
    const currentCount = manualTopicOrder.length;
    let rafId = null;
    let rafId2 = null;

    if (currentCount > previousCount) {
      const addedManualId = manualTopicOrder[currentCount - 1];

      rafId = window.requestAnimationFrame(() => {
        rafId2 = window.requestAnimationFrame(() => {
          const manualCardNode = manualCardRefs.current.get(addedManualId);
          const scrollContainerNode = draftScrollContainerRef.current;

          if (!manualCardNode) return;

          if (scrollContainerNode) {
            const manualCardRect = manualCardNode.getBoundingClientRect();
            const scrollContainerRect = scrollContainerNode.getBoundingClientRect();
            const cardIsTallerThanCanvas = manualCardRect.height >= scrollContainerRect.height;

            if (cardIsTallerThanCanvas) {
              const computedStyles = window.getComputedStyle(manualCardNode);
              const marginTop = Number.parseFloat(computedStyles.marginTop) || 0;
              const manualCardTopRelativeToViewport = manualCardRect.top;
              const scrollContainerTopRelativeToViewport = scrollContainerRect.top;
              const nextScrollTop =
                manualCardTopRelativeToViewport -
                scrollContainerTopRelativeToViewport +
                scrollContainerNode.scrollTop -
                marginTop;

              scrollContainerNode.scrollTo({
                behavior: 'smooth',
                left: 0,
                top: nextScrollTop,
              });
              return;
            }
          }

          manualCardNode.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        });
      });
    }

    previousManualCountRef.current = currentCount;

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (rafId2) window.cancelAnimationFrame(rafId2);
    };
  }, [manualTopicOrder]);

  useEffect(() => {
    const idsToFetch = Array.from(new Set([...componentIds, activeConfigTopicId])).filter(
      (id) => id != null && !draftDatasetMap[id]
    );
    if (!idsToFetch.length) return;

    const controller = new AbortController();

    const fetchDraftTopics = async () => {
      if (idsToFetch.some((id) => String(id) === String(activeConfigTopicId))) {
        setActiveConfigPreviewErrorTopicId(null);
      }

      try {
        const data = await readDraftTopics({
          componentIds: idsToFetch,
          signal: controller.signal,
        });

        setDraftDatasetMap((prev) => {
          const resolvedData = idsToFetch.reduce((acc, componentId) => {
            acc[componentId] = data?.[componentId] || { dataset: null };
            return acc;
          }, {});

          return { ...prev, ...resolvedData };
        });
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
        if (idsToFetch.some((id) => String(id) === String(activeConfigTopicId))) {
          setActiveConfigPreviewErrorTopicId(activeConfigTopicId);
        }
        console.error('Error fetching draft topics:', error);
      }
    };

    fetchDraftTopics();

    return () => {
      controller.abort();
    };
  }, [activeConfigTopicId, componentIds, draftDatasetMap]);

  const handlePromptModalVisibilityChange = (nextIsOpen) => {
    if (!nextIsOpen) {
      if (!shouldPersistPromptChangesRef.current && promptSelectionsSnapshotRef.current) {
        const {
          draftIdentitySignature: snapshotDraftIdentitySignature,
          flowStateNarrativeName: snapshotFlowStateNarrativeName,
          name,
          selectedAnswers: answersSnapshot,
          transformedAnswersMap: transformedAnswersSnapshot,
        } = promptSelectionsSnapshotRef.current;

        if (snapshotDraftIdentitySignature === draftIdentitySignature) {
          if (snapshotFlowStateNarrativeName === flowStateNarrativeName) {
            setPromptModalName(name);
          }
          setSelectedAnswers(answersSnapshot);
          setTransformedAnswersMap(transformedAnswersSnapshot);
        }
      }

      shouldPersistPromptChangesRef.current = false;
      promptSelectionsSnapshotRef.current = null;
    }

    setIsPromptModalOpen(nextIsOpen);
  };

  const handleSelectPrompts = (prompt = null) => {
    if (!canSelectPrompts) return;

    setBuildError(null);
    promptSelectionsSnapshotRef.current = {
      draftIdentitySignature,
      flowStateNarrativeName,
      name: promptModalName,
      selectedAnswers,
      transformedAnswersMap,
    };
    shouldPersistPromptChangesRef.current = false;
    if (prompt) {
      const promptKey = prompt?.promptKey || prompt?.id || null;
      const resolvedPrompt =
        visiblePrompts.find(
          (visiblePrompt) => visiblePrompt.promptKey === promptKey || visiblePrompt.id === promptKey
        ) || prompt;
      setActivePrompt(resolvedPrompt);
    }
    setIsPromptModalOpen(true);
  };

  const handleRunReprompt = useCallback((prompt = null) => {
    setSelectedRunPrompt(prompt);
    setIsPromptModalOpen(true);
  }, []);

  const handleRunNarrativeNameChange = useCallback(
    ({ flowId: renamedFlowId, name }) => {
      if (typeof onNarrativeNameChange === 'function' && chatSessionId) {
        onNarrativeNameChange({
          chatSessionId,
          flowId: renamedFlowId,
          name,
        });
      }
    },
    [chatSessionId, onNarrativeNameChange]
  );

  const handleRunModalVisibilityChange = useCallback((nextIsOpen) => {
    if (!nextIsOpen) {
      setSelectedRunPrompt(null);
    }
    setIsPromptModalOpen(nextIsOpen);
  }, []);

  const handleBuildFromPrompts = async () => {
    if (isBuildDisabled || isBuilding) return;

    const promptAnswers = buildPromptAnswersPayload(
      transformedAnswersMap,
      Object.entries({
        ...(flowState?.defaultPromptAnswers || {}),
        ...(flowState?.readOnlyPromptAnswers || {}),
      }).reduce((acc, [field, value]) => {
        const isVisiblePrompt = allVisiblePrompts.some((prompt) => prompt.promptKey === field);
        if (!isVisiblePrompt) {
          acc[field] = value;
        }
        return acc;
      }, {})
    );

    if (!Object.keys(promptAnswers).length) {
      const errorMessage = 'Please complete the required prompts before building your Narrative.';
      setBuildError(errorMessage);
      toast.error(errorMessage);
      return;
    }

    setBuildError(null);
    // Invalidate any in-flight draft save so delayed responses cannot overwrite run state.
    draftPersistenceRequestIdRef.current += 1;
    setPendingBuildFlowId(null);
    setIsBuilding(true);

    try {
      const projectId = project?.id ?? (await readProject({ chatSessionId }))?.id;
      if (!projectId) throw new Error('Project ID is required to create a Narrative.');

      const createdNarrative = await createNarrative({
        flowId,
        id: 'dynamic',
        projectId,
        promptAnswers,
        title: draftNarrativeName,
        topicIds: componentIds,
      });

      if (!createdNarrative?.flowId) {
        throw new Error('Narrative build did not return a flow identifier.');
      }

      if (chatSessionId) {
        await updateFlowState({
          chatSessionId,
          flowState: {
            narrativeName: draftNarrativeName,
          },
        });
      }

      if (onFlowStateChange) {
        onFlowStateChange({
          ...(flowState || {}),
          editablePromptAnswers: draftEditablePromptAnswers,
          focusFlowId: createdNarrative.flowId,
          narrativeName: draftNarrativeName,
          topics: componentIds,
        });
      }

      setPendingBuildFlowId(createdNarrative.flowId);
      if (typeof onNarrativeCreated === 'function' && chatSessionId) {
        onNarrativeCreated({
          chatSessionId,
          flowId: createdNarrative.flowId,
          name: draftNarrativeName,
        });
      }

      if (onFlowSelected) {
        onFlowSelected(createdNarrative.flowId);
      }

      if (flowId == null) {
        try {
          await deleteNarrativeDraft({ projectId });
          onDraftConsumed?.({ chatSessionId, projectId });
        } catch (error) {
          console.error('Narrative was created but its source Draft could not be removed', error);
          toast.error('Narrative created, but the source Draft could not be removed.');
        }
      }

      shouldPersistPromptChangesRef.current = true;
      handlePromptModalVisibilityChange(false);
    } catch (error) {
      const promptErrors = error?.response?.data?.['Prompt Errors'] ?? [];
      const errorMessage =
        promptErrors[0] || 'There was an error building your Narrative. Please try again.';

      setBuildError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to build Narrative:', error);
    } finally {
      setIsBuilding(false);
    }
  };

  const registerManualCardRef = useCallback((componentId) => {
    if (componentId == null) return null;

    if (!manualCardRefCallbacks.current.has(componentId)) {
      manualCardRefCallbacks.current.set(componentId, (node) => {
        if (node) {
          manualCardRefs.current.set(componentId, node);
        } else {
          manualCardRefs.current.delete(componentId);
          manualCardRefCallbacks.current.delete(componentId);
        }
      });
    }

    return manualCardRefCallbacks.current.get(componentId);
  }, []);

  const handleToolbarAction = (buttonType) => {
    if (isBuilding || isToolbarActionLoading) return;

    if (isViewingRun) {
      buttonType === 'primary' ? handleRunReprompt() : setDraftPendingDeletion(true);

      return;
    }

    if (!isViewingRun && buttonType === 'default') {
      setDraftPendingDeletion(true);
      return;
    }

    if (canCreateNarrative) {
      handleBuildFromPrompts();
      return;
    }

    handleSelectPrompts();
  };

  const handleDraftTopicAction = useCallback(
    async (action, topic) => {
      if (!canPersistDraftOrder || !topic) return;
      const currentIds = flowStateTopicIds;
      const topicId = topic.componentId;
      const currentIndex = currentIds.indexOf(topicId);
      if (currentIndex === -1) return;

      const nextIds = [...currentIds];

      if (action === 'up' && currentIndex > 0) {
        [nextIds[currentIndex - 1], nextIds[currentIndex]] = [
          nextIds[currentIndex],
          nextIds[currentIndex - 1],
        ];
      } else if (action === 'down' && currentIndex < nextIds.length - 1) {
        [nextIds[currentIndex + 1], nextIds[currentIndex]] = [
          nextIds[currentIndex],
          nextIds[currentIndex + 1],
        ];
      } else if (action === 'remove') {
        nextIds.splice(currentIndex, 1);
      } else {
        return;
      }

      const noChange =
        nextIds.length === currentIds.length &&
        nextIds.every((id, index) => id === currentIds[index]);
      if (noChange) return;

      setPendingDraftAction({ action, topicId });
      try {
        await persistTopicOrder(nextIds);
        if (action === 'remove' && typeof onSelectedTopicsChange === 'function') {
          onSelectedTopicsChange((previousTopics) => {
            if (!Array.isArray(previousTopics)) return previousTopics;
            const nextTopics = previousTopics.filter(
              (topic) => String(topic?.id) !== String(topicId)
            );
            const didManualTopicsChange = nextTopics.length !== previousTopics.length;
            manualOrderChangeSourceRef.current = didManualTopicsChange;
            return didManualTopicsChange ? nextTopics : previousTopics;
          });
          trackTopicCardAddRemove({
            action: 'remove',
            chatSessionId,
            environmentState: 'draft',
            sourceOfChange: 'manual',
            topicId,
            topicName: topic.name,
          });
        }
      } catch (error) {
        console.error('Failed to update draft topic order', error);
        // toast already shown inside persistTopicOrder
      } finally {
        setPendingDraftAction(null);
      }
    },
    [
      canPersistDraftOrder,
      chatSessionId,
      flowStateTopicIds,
      onSelectedTopicsChange,
      persistTopicOrder,
    ]
  );

  const handleManualTopicAction = useCallback(
    (action, topic) => {
      if (typeof onSelectedTopicsChange !== 'function' || !topic) return;
      const topicId = topic.componentId;
      let nextManualTopics = null;
      manualOrderChangeSourceRef.current = true;

      onSelectedTopicsChange((prevTopics) => {
        if (!Array.isArray(prevTopics)) return prevTopics;
        const index = prevTopics.findIndex((topic) => topic?.id === topicId);
        if (index === -1) return prevTopics;

        const nextTopics = [...prevTopics];

        if (action === 'remove') {
          nextTopics.splice(index, 1);
        } else if (action === 'up' && index > 0) {
          [nextTopics[index - 1], nextTopics[index]] = [nextTopics[index], nextTopics[index - 1]];
        } else if (action === 'down' && index < nextTopics.length - 1) {
          [nextTopics[index + 1], nextTopics[index]] = [nextTopics[index], nextTopics[index + 1]];
        } else {
          manualOrderChangeSourceRef.current = false;
          return prevTopics;
        }

        nextManualTopics = nextTopics;
        return nextTopics;
      });

      if (nextManualTopics) {
        const manualIds = nextManualTopics.map((topic) => topic.id);
        const combinedOrder = mergeUniqueValues(flowStateTopicIds, manualIds);
        if (!combinedOrder.length) {
          manualOrderChangeSourceRef.current = false;
          return;
        }
        persistTopicOrder(combinedOrder)
          .catch(() => {})
          .finally(() => {
            manualOrderChangeSourceRef.current = false;
          });
        if (action === 'remove') {
          trackTopicCardAddRemove({
            action: 'remove',
            chatSessionId,
            environmentState: 'draft',
            sourceOfChange: 'manual',
            topicId,
            topicName: topic.name,
          });
        }
      } else {
        manualOrderChangeSourceRef.current = false;
      }
    },
    [chatSessionId, flowStateTopicIds, onSelectedTopicsChange, persistTopicOrder]
  );

  const handleConfigVariantChange = useCallback(
    (cardId, key, value) => {
      if (!activeConfigCard?.id || activeConfigCard.id !== cardId) return;
      if (typeof onActiveTopicConfigurationChange !== 'function') return;
      // Apply primary/secondary fallback only for two-dimension BYO groups.
      // One-dimension groups continue to use exact-match selection.
      const variantDimensions = Array.isArray(activeConfigCard?.config?.variantDimensions)
        ? activeConfigCard.config.variantDimensions
        : [];
      const primaryDimension = variantDimensions[0];
      const hasTwoDimensions = variantDimensions.length === 2;
      const nextSelectedVariant = {
        ...(activeConfigCard?.selectedVariant || {}),
        [key]: value,
      };

      if (hasTwoDimensions && key === primaryDimension) {
        const secondaryDimension = variantDimensions[1];
        const secondaryOptions = Array.isArray(
          activeConfigCard?.config?.variantOptions?.[secondaryDimension]
        )
          ? activeConfigCard.config.variantOptions[secondaryDimension]
          : [];
        const currentSecondaryValue = nextSelectedVariant?.[secondaryDimension];
        const orderedSecondaryOptions = [
          ...(currentSecondaryValue != null ? [currentSecondaryValue] : []),
          ...secondaryOptions.filter((option) => option !== currentSecondaryValue),
        ];

        const resolvedSecondaryValue = orderedSecondaryOptions.find((option) => {
          const candidateVariant = {
            ...nextSelectedVariant,
            [secondaryDimension]: option,
          };
          const candidateSignature = getVariantSignature(
            candidateVariant,
            activeConfigCard?.config?.signatureDimensions
          );
          const candidateTopic = activeConfigCard?.config?.variantMap?.[candidateSignature];

          return (
            candidateTopic?.id != null &&
            (!activeConfigOccupiedTopicIdSet?.has(String(candidateTopic.id)) ||
              String(candidateTopic.id) === String(activeConfigCard?.previousTopicId))
          );
        });

        if (!resolvedSecondaryValue) return;

        nextSelectedVariant[secondaryDimension] = resolvedSecondaryValue;
      }

      const nextSignature = getVariantSignature(
        nextSelectedVariant,
        activeConfigCard?.config?.signatureDimensions
      );
      const nextTopic = activeConfigCard?.config?.variantMap?.[nextSignature];

      if (
        !nextTopic?.id ||
        (activeConfigOccupiedTopicIdSet?.has(String(nextTopic.id)) &&
          String(nextTopic.id) !== String(activeConfigCard?.previousTopicId))
      ) {
        return;
      }

      onActiveTopicConfigurationChange({
        ...activeConfigCard,
        selectedVariant: nextSelectedVariant,
      });
    },
    [activeConfigCard, activeConfigOccupiedTopicIdSet, onActiveTopicConfigurationChange]
  );

  const closeActiveConfigModal = useCallback(() => {
    setDraftDatasetMap((prev) => {
      const latestComponentIds = componentIdsRef.current || [];

      if (
        activeConfigTopicId == null ||
        latestComponentIds.some(
          (componentId) => String(componentId) === String(activeConfigTopicId)
        ) ||
        !prev?.[activeConfigTopicId]
      ) {
        return prev;
      }

      const nextDraftDatasetMap = { ...prev };
      delete nextDraftDatasetMap[activeConfigTopicId];
      return nextDraftDatasetMap;
    });
    onActiveTopicConfigurationChange?.(null);
  }, [activeConfigTopicId, onActiveTopicConfigurationChange]);

  const handleSaveConfigCard = useCallback(
    async (card) => {
      const previousTopicId = card?.previousTopicId;
      if (!previousTopicId && checkIsTopicCardCountMax(occupiedTopicIdSet.size)) return;
      if (!card?.id) return;
      const selectedSignature = getVariantSignature(
        card?.selectedVariant,
        card?.config?.signatureDimensions
      );
      const nextTopic = card?.config?.variantMap?.[selectedSignature];
      if (!nextTopic?.id) {
        toast.error('Choose an available combination.');
        return;
      }

      const isChanged = previousTopicId ? String(previousTopicId) !== String(nextTopic.id) : true;
      if (previousTopicId && !isChanged) {
        closeActiveConfigModal();
        return;
      }

      if (card?.mode === 'run') {
        if (!previousTopicId) {
          if (!onInsertRunTopicConfiguration) return;
          const wasInserted = await onInsertRunTopicConfiguration({ topic: nextTopic });
          if (wasInserted !== false) {
            closeActiveConfigModal();
          }
          return;
        }

        onReplaceRunTopicConfiguration?.({
          nextTopic,
          previousTopicId,
        });
        closeActiveConfigModal();
        return;
      }

      shouldPersistDraftCardChangesRef.current = true;
      void (async () => {
        const resolvedChatSessionId = chatSessionId || (await onEnsureChatSession?.());
        if (!resolvedChatSessionId) return;
        if (previousTopicId) {
          const previousTopicName = topicConfigurationLookup[previousTopicId]?.name ?? 'n/a';
          trackTopicCardAddRemove({
            action: 'remove',
            chatSessionId: resolvedChatSessionId,
            environmentState: 'draft',
            sourceOfChange: 'manual',
            topicId: previousTopicId,
            topicName: previousTopicName,
          });
        }
        trackTopicCardAddRemove({
          action: 'add',
          chatSessionId: resolvedChatSessionId,
          environmentState: 'draft',
          sourceOfChange: 'manual',
          topicId: nextTopic.id,
          topicName: nextTopic?.name ?? 'n/a',
        });
      })();

      const isFlowStateDraftTopic =
        previousTopicId != null &&
        flowStateTopicIds.some((componentId) => String(componentId) === String(previousTopicId));

      if (isFlowStateDraftTopic && typeof onFlowStateChange === 'function') {
        const nextFlowState = {
          ...(flowState || {}),
          topics: flowStateTopicIds.map((componentId) =>
            String(componentId) === String(previousTopicId) ? nextTopic.id : componentId
          ),
        };

        onFlowStateChange(nextFlowState);

        if (typeof onSelectedTopicsChange === 'function') {
          onSelectedTopicsChange((previous) => {
            if (!Array.isArray(previous)) return previous;
            return previous.filter((item) => String(item?.id) !== String(previousTopicId));
          });
        }

        closeActiveConfigModal();
        return;
      }

      if (typeof onSelectedTopicsChange !== 'function') return;

      onSelectedTopicsChange((previous) => {
        if (!previousTopicId) {
          if (previous.some((item) => String(item?.id) === String(nextTopic.id))) return previous;
          return [
            ...previous,
            {
              configMeta: card.config,
              id: nextTopic.id,
              name: nextTopic.name,
              selectedVariant: card.selectedVariant,
              variantSignature: selectedSignature,
              vizType: nextTopic.vizType,
            },
          ];
        }

        return previous.map((item) => {
          if (String(item?.id) !== String(previousTopicId)) return item;
          return {
            ...item,
            configMeta: card.config,
            id: nextTopic.id,
            name: nextTopic.name,
            selectedVariant: card.selectedVariant,
            variantSignature: selectedSignature,
            vizType: nextTopic.vizType,
          };
        });
      });
      closeActiveConfigModal();
    },
    [
      chatSessionId,
      closeActiveConfigModal,
      flowState,
      flowStateTopicIds,
      onEnsureChatSession,
      onFlowStateChange,
      onInsertRunTopicConfiguration,
      onReplaceRunTopicConfiguration,
      onSelectedTopicsChange,
    ]
  );

  const handleCancelConfigCard = useCallback(
    (card) => {
      if (!card?.id) return;
      closeActiveConfigModal();
    },
    [closeActiveConfigModal]
  );

  const openTopicConfiguration = useCallback(
    async ({
      fallbackTitle,
      lookupKey,
      mode,
      occupiedTopicIds = null,
      previousTopicId,
      resolvedTopicConfiguration = null,
      vizType = null,
    }) => {
      if (!lookupKey || typeof onActiveTopicConfigurationChange !== 'function') return;

      let nextResolvedTopicConfiguration =
        resolvedTopicConfiguration || topicConfigurationLookup?.[String(lookupKey)];

      if (
        !nextResolvedTopicConfiguration &&
        typeof onRequestTopicConfigurationLookup === 'function'
      ) {
        const groups = await onRequestTopicConfigurationLookup();
        const requestedLookup = buildTopicConfigurationLookup(groups);
        nextResolvedTopicConfiguration = requestedLookup?.[String(lookupKey)];
      }

      const configMeta = nextResolvedTopicConfiguration?.config;
      if (!hasMultipleConfigVariants(configMeta)) return;
      const effectiveOccupiedTopicIdSet = Array.isArray(occupiedTopicIds)
        ? buildOccupiedTopicIdSet(occupiedTopicIds)
        : occupiedTopicIdSet;

      const currentSelectedVariant = nextResolvedTopicConfiguration?.selectedVariant || {};
      const initialSelectedVariant = findFirstAvailableVariantSelection({
        config: configMeta,
        occupiedTopicIdSet: effectiveOccupiedTopicIdSet,
        preferredVariant: currentSelectedVariant,
        previousTopicId,
      });

      onActiveTopicConfigurationChange({
        config: configMeta,
        id: `config:${configMeta.grouping}:${previousTopicId}`,
        mode,
        name: nextResolvedTopicConfiguration?.name || fallbackTitle,
        occupiedTopicIds: Array.from(effectiveOccupiedTopicIdSet),
        previousTopicId,
        selectedVariant: initialSelectedVariant,
        vizType: nextResolvedTopicConfiguration?.vizType || vizType,
      });
    },
    [
      onActiveTopicConfigurationChange,
      onRequestTopicConfigurationLookup,
      occupiedTopicIdSet,
      topicConfigurationLookup,
    ]
  );

  const handleEditConfigCard = useCallback(
    async (draft) => {
      if (!draft?.componentId || typeof onActiveTopicConfigurationChange !== 'function') return;

      const selectedTopicCard = selectedTopicCards.find(
        (item) => String(item?.id) === String(draft.componentId)
      );
      let resolvedTopicConfiguration = selectedTopicCard?.configMeta
        ? {
            config: selectedTopicCard.configMeta,
            name: selectedTopicCard?.name || draft.title,
            selectedVariant: selectedTopicCard?.selectedVariant || draft?.selectedVariant || {},
            vizType: selectedTopicCard?.vizType || draft.visualizationType,
          }
        : draft?.configMeta
          ? {
              config: draft.configMeta,
              name: selectedTopicCard?.name || draft.title,
              selectedVariant: selectedTopicCard?.selectedVariant || draft?.selectedVariant || {},
              vizType: selectedTopicCard?.vizType || draft.visualizationType,
            }
          : null;

      await openTopicConfiguration({
        fallbackTitle: selectedTopicCard?.name || draft.title,
        lookupKey: draft.componentId,
        mode: 'draft',
        previousTopicId: draft.componentId,
        resolvedTopicConfiguration,
        vizType: selectedTopicCard?.vizType || draft.visualizationType,
      });
    },
    [onActiveTopicConfigurationChange, openTopicConfiguration, selectedTopicCards]
  );

  const activeConfigApiDraft =
    activeConfigTopicId != null ? draftDatasetMap?.[activeConfigTopicId] || null : null;
  const hasActiveConfigPreviewError =
    activeConfigTopicId != null &&
    String(activeConfigPreviewErrorTopicId) === String(activeConfigTopicId);
  const isActiveConfigPreviewLoading =
    activeConfigTopicId != null &&
    !hasActiveConfigPreviewError &&
    !Object.prototype.hasOwnProperty.call(draftDatasetMap, activeConfigTopicId);
  const activeConfigPreviewDraft =
    activeConfigTopic && !isActiveConfigPreviewLoading && !hasActiveConfigPreviewError
      ? {
          cleanTitle:
            activeConfigApiDraft?.cleanTitle || activeConfigTopic?.name || activeConfigCard?.title,
          componentId: activeConfigTopicId,
          dataset: activeConfigApiDraft?.dataset
            ? {
                ...activeConfigApiDraft.dataset,
                isDraftDisplayData: true,
                type:
                  activeConfigApiDraft.dataset.type ||
                  activeConfigTopic?.vizType ||
                  activeConfigCard?.vizType ||
                  null,
              }
            : null,
          sampleSize:
            typeof activeConfigApiDraft?.sampleSize === 'number'
              ? activeConfigApiDraft.sampleSize
              : null,
          title:
            activeConfigApiDraft?.cleanTitle ||
            activeConfigApiDraft?.title ||
            activeConfigCard?.title,
          visualizationType:
            activeConfigApiDraft?.dataset?.type ||
            activeConfigTopic?.vizType ||
            activeConfigCard?.vizType ||
            null,
        }
      : null;
  const isActiveConfigUnchanged =
    activeConfigCard?.previousTopicId != null &&
    String(activeConfigCard.previousTopicId) === String(activeConfigTopicId);
  const isActiveConfigTopicAlreadyAdded =
    activeConfigTopicId != null &&
    activeConfigOccupiedTopicIdSet.has(String(activeConfigTopicId)) &&
    String(activeConfigTopicId) !== String(activeConfigCard?.previousTopicId);
  const isActiveConfigSaveDisabled =
    !activeConfigTopicId || isActiveConfigTopicAlreadyAdded || isActiveConfigUnchanged;
  const activeConfigVariantDimensions = Array.isArray(activeConfigCard?.config?.variantDimensions)
    ? activeConfigCard.config.variantDimensions
    : [];
  const activeConfigSummary = [
    activeConfigCard?.config?.grouping || activeConfigCard?.title,
    ...activeConfigVariantDimensions.map((dimension) => {
      const selectedValue = activeConfigCard?.selectedVariant?.[dimension];
      return typeof selectedValue === 'string' && selectedValue.trim().length > 0
        ? `${dimension}: ${selectedValue}`
        : null;
    }),
  ].filter((part) => typeof part === 'string' && part.trim().length > 0);

  const configureTopicModal = activeConfigCard ? (
    <ConfigureModal
      activeConfigCard={activeConfigCard}
      activeConfigPreviewDraft={activeConfigPreviewDraft}
      activeConfigSummary={activeConfigSummary}
      isPreviewLoading={isActiveConfigPreviewLoading}
      isSaveDisabled={isActiveConfigSaveDisabled}
      occupiedTopicIdSet={activeConfigOccupiedTopicIdSet}
      onCancel={handleCancelConfigCard}
      onSave={handleSaveConfigCard}
      onVariantChange={handleConfigVariantChange}
    />
  ) : null;

  const handleRunTopicAction = useCallback(
    async ({ action, flowTopics = [], isDeletingLastRunTopic = false, setFlow, topic }) => {
      if (
        !isViewingRun ||
        !topic?.id ||
        typeof setFlow !== 'function' ||
        isNarrativeProcessing ||
        isRunTopicMutationInProgress ||
        Boolean(pendingRunTopicAction)
      )
        return;

      const flowTopicId = topic.id;
      const topicsList = Array.isArray(flowTopics) ? flowTopics : [];
      const originalTopics = topicsList.slice();
      let hasDeletedNarrative = false;
      const applyTopics = (nextTopics) => {
        setFlow((prevFlow) => {
          if (!prevFlow) return prevFlow;
          return { ...prevFlow, topics: nextTopics };
        });
      };

      setPendingRunTopicAction({ action, topicId: flowTopicId });

      try {
        if ((action === 'up' || action === 'down') && flowId) {
          const visibleTopics = topicsList.filter(shouldShowRunTopicCard);
          const visibleIndex = visibleTopics.findIndex((item) => item?.id === flowTopicId);
          if (visibleIndex === -1) return;

          const destinationVisibleIndex = action === 'up' ? visibleIndex - 1 : visibleIndex + 1;
          if (destinationVisibleIndex < 0 || destinationVisibleIndex >= visibleTopics.length)
            return;

          const currentIndex = topicsList.findIndex((item) => item?.id === flowTopicId);
          const destinationIndex = topicsList.findIndex(
            (item) => item?.id === visibleTopics[destinationVisibleIndex]?.id
          );
          if (currentIndex === -1 || destinationIndex === -1) return;

          const nextTopics = topicsList.slice();
          [nextTopics[currentIndex], nextTopics[destinationIndex]] = [
            nextTopics[destinationIndex],
            nextTopics[currentIndex],
          ];
          applyTopics(nextTopics);
          await reorderFlowTopics({
            flowId,
            topicIds: nextTopics.map((item) => item.id),
          });
        } else if (action === 'delete') {
          const nextTopics = topicsList.filter((item) => item.id !== flowTopicId);
          applyTopics(nextTopics);
          trackTopicCardAddRemove({
            action: 'remove',
            chatSessionId,
            environmentState: 'completed_narrative',
            flowId,
            sourceOfChange: 'manual',
            topicId: topic.topicId,
            topicName: topic.name,
          });

          if (isDeletingLastRunTopic && flowId) {
            await clearNarrativeFlow({ flowId });
            await deleteNarratives({ ids: [flowId] });
            hasDeletedNarrative = true;

            // focusFlowId may briefly point to the previously selected run. Any non-null
            // value represents run-focused state, so clear it when the active run is deleted.
            const shouldClearRunFlowState = flowState?.focusFlowId != null;

            if (typeof onNarrativeDeleted === 'function') {
              onNarrativeDeleted({
                chatSessionId,
                flowId,
                shouldClearCanvasState: true,
                shouldClearRunFlowState,
              });
            }

            if (typeof onFlowSelected === 'function') {
              onFlowSelected(null);
            }

            return;
          }

          await softDeleteFlowTopic({ topicId: flowTopicId });
        } else {
          return;
        }
      } catch (error) {
        console.error('Failed to process run topic action', error);
        toast.error('We could not complete that action. Please try again.');
        if (!hasDeletedNarrative) {
          applyTopics(originalTopics);
        }
      } finally {
        setPendingRunTopicAction(null);
        if (action === 'delete') {
          setTopicPendingDeletion(null);
        }
      }
    },
    [
      chatSessionId,
      clearNarrativeFlow,
      flowId,
      flowState?.focusFlowId,
      flowStateTopicIds,
      isNarrativeProcessing,
      isRunTopicMutationInProgress,
      isViewingRun,
      onNarrativeDeleted,
      onFlowSelected,
      pendingRunTopicAction,
      setTopicPendingDeletion,
    ]
  );

  const runTopicControlsBuilder = useCallback(
    ({ setFlow, topic, topics }) => {
      if (!isViewingRun || !topic?.id || !Array.isArray(topics)) return null;
      const visibleTopics = topics.filter(shouldShowRunTopicCard);
      const visibleIndex = visibleTopics.findIndex((item) => item?.id === topic?.id);
      const totalTopics = visibleTopics.length;
      if (visibleIndex === -1) return null;
      const topicTags = topic?.tags || {};
      const isNoSample = flowTopicTag?.NO_SAMPLE in topicTags;
      const isInternalError = narrativeErrorStatuses.includes(topic?.status);
      const shouldShowRemoveAction =
        canManageRunTopics &&
        (isViewingRun || Boolean(topic?.isHidden) || isNoSample || isInternalError);
      const isPending = Boolean(pendingRunTopicAction);
      const shouldDisableRunControls =
        isPending || isNarrativeProcessing || isRunTopicMutationInProgress;
      const controls = {
        primaryAction: null,
        secondaryActions: canManageRunTopics
          ? [
              {
                disabled: shouldDisableRunControls || totalTopics < 2 || visibleIndex <= 0,
                label: 'Move Up',
                onClick: () =>
                  handleRunTopicAction({
                    action: 'up',
                    flowTopics: topics,
                    setFlow,
                    topic,
                  }),
              },
              {
                disabled:
                  shouldDisableRunControls || totalTopics < 2 || visibleIndex === totalTopics - 1,
                label: 'Move Down',
                onClick: () =>
                  handleRunTopicAction({
                    action: 'down',
                    flowTopics: topics,
                    setFlow,
                    topic,
                  }),
              },
            ]
          : [],
      };

      if (shouldShowRemoveAction) {
        const removeAction = {
          disabled: shouldDisableRunControls,
          label: 'Remove',
          onClick: () =>
            setTopicPendingDeletion({
              flowTopics: topics,
              isDeletingLastRunTopic: totalTopics === 1,
              setFlow,
              topic,
            }),
        };
        controls.secondaryActions.unshift(removeAction);
      }
      return controls;
    },
    [
      canManageRunTopics,
      handleRunTopicAction,
      isNarrativeProcessing,
      isRunTopicMutationInProgress,
      isViewingRun,
      pendingRunTopicAction,
      setTopicPendingDeletion,
    ]
  );

  const preBuildModal =
    !isViewingRun && canSelectPrompts ? (
      <PreBuildModal
        activePrompt={activePrompt}
        buildError={buildError}
        getPillTooltip={getPromptPillTooltip}
        getPillValue={getPromptPillValue}
        handleBuild={handleBuildFromPrompts}
        headerIcon={null}
        isBuildDisabled={isBuildDisabled}
        isBuilding={isBuilding}
        isLoading={false}
        isModalOpen={isPromptModalOpen}
        name={promptModalName}
        prompts={visiblePrompts}
        routeId="dynamic"
        routeLabel={DEFAULT_PROMPT_ROUTE_LABEL}
        sameLevelPrompts={flowState?.sameLevelPrompts || []}
        searchPrompts={flowState?.searchPrompts || []}
        selectedAnswers={selectedAnswers}
        setActivePrompt={setActivePrompt}
        setIsModalOpen={handlePromptModalVisibilityChange}
        setName={setPromptModalName}
        setSelectedAnswers={setSelectedAnswers}
        setTransformedAnswersMap={setTransformedAnswersMap}
        topicIds={componentIds}
        transformedAnswersMap={transformedAnswersMap}
        transformPromptSelections={transformPromptSelections}
        type="Narrative"
      />
    ) : null;

  const handleDeleteDraftAction = async () => {
    try {
      setIsClearingCanvas(true);
      if (isViewingRun) {
        await clearNarrativeFlow({ flowId });
      } else {
        const projectId = project?.id ?? (await readProject({ chatSessionId }))?.id;
        await clearNarrativeDraft({ projectId });
      }

      const emptyDraftFlowState = {
        ...(flowState || {}),
        editablePromptAnswers: {},
        focusFlowId: null,
        narrativeName: DEFAULT_DRAFT_NARRATIVE_NAME,
        topics: [],
      };

      onFlowStateChange?.(emptyDraftFlowState);
      onSelectedTopicsChange?.([]);
      onActiveTopicConfigurationChange?.(null);

      if (isViewingRun) {
        onFlowSelected?.(null);
      }

      onDraftCleared?.({ chatSessionId });

      setSelectedAnswers({});
      setTransformedAnswersMap({});
      setPromptModalName(DEFAULT_DRAFT_NARRATIVE_NAME);
      setDraftPendingDeletion(null);
    } catch (error) {
      console.error('Failed to clear the Narrative canvas', error);
      isViewingRun
        ? toast.error("We couldn't create a new Narrative. Please try again.")
        : toast.error("We couldn't clear the canvas. Please try again.");
    } finally {
      setIsClearingCanvas(false);
    }
  };
  const deleteTopicCardConfirmationModal = topicPendingDeletion ? (
    <ConfirmActionModal
      confirmText={topicPendingDeletion?.isDeletingLastRunTopic ? 'Remove and Delete' : 'Remove'}
      confirmVariant="error"
      isActive={Boolean(topicPendingDeletion)}
      isConfirmDisabled={
        pendingRunTopicAction?.action === 'delete' &&
        pendingRunTopicAction?.topicId === topicPendingDeletion?.topic?.id
      }
      message={
        topicPendingDeletion?.isDeletingLastRunTopic
          ? 'Are you sure you would like to remove this topic card and delete this Narrative? This cannot be undone.'
          : "Are you sure you would like to remove this topic card? You'll lose its results."
      }
      onCancel={() => setTopicPendingDeletion(null)}
      onConfirm={() =>
        handleRunTopicAction({
          ...topicPendingDeletion,
          action: 'delete',
        })
      }
      title={
        topicPendingDeletion?.isDeletingLastRunTopic ? 'Remove and Delete Narrative' : 'Remove'
      }
    />
  ) : null;

  const deleteDraftNarrativeConfirmationModal = draftPendingDeletion ? (
    <ConfirmActionModal
      confirmText={isViewingRun ? 'Proceed' : 'Clear Canvas'}
      confirmVariant={isViewingRun ? 'primary' : 'error'}
      isActive={Boolean(draftPendingDeletion)}
      isCancelDisabled={isClearingCanvas}
      isConfirmDisabled={isClearingCanvas}
      message={`This will clear the canvas so you can start from scratch. ${hasProjectDraft && isViewingRun ? 'Existing drafts in this project will be permanently deleted.' : ''} Run Narratives are not affected and stay accessible in project history.`}
      onCancel={() => setDraftPendingDeletion(null)}
      onConfirm={() => handleDeleteDraftAction()}
      title={isViewingRun ? 'Create New Narrative' : 'Clear Canvas'}
    />
  ) : null;

  const handleFavoriteProject = (isFavorite) => {
    if (!chatSessionId) return;

    favoriteProject({ chatSessionId, isFavorite })
      .then(() => {
        setProject((prevProject) => {
          if (!prevProject) return prevProject;
          return { ...prevProject, isFavorite };
        });
        if (typeof onFavoriteChange === 'function') {
          onFavoriteChange(chatSessionId, isFavorite);
        }
      })
      .catch((error) => {
        console.error('Failed to update favorite project status', error);
        toast.error('We could not update your favorite project. Please try again.');
      });
  };

  const renderProjectTitle = () => {
    if (!project) return null;

    const isFavorite = Boolean(project?.isFavorite);

    return (
      <div className={styles['project-name-banner']}>
        <button
          aria-label={isFavorite ? 'Remove project from favorites' : 'Add project to favorites'}
          className={styles['project-name-favorite-button']}
          onClick={() => handleFavoriteProject(!isFavorite)}
          type="button"
        >
          <FavoriteToggle isFavorite={isFavorite} />
        </button>
        <EditableTitle
          className={styles['project-name-editable']}
          onBlur={handleProjectNameBlur}
          onChange={handleProjectNameChange}
          value={project?.name ?? DEFAULT_PROJECT_NAME}
        />
      </div>
    );
  };

  const handleProjectNameChange = (nextName) => {
    isExternalProjectNameUpdateRef.current = false;
    if (typeof onProjectNameChange === 'function' && nextName?.trim()) {
      onProjectNameChange(chatSessionId, nextName);
    }
    setProject((prevProject) => {
      if (!prevProject) return prevProject;
      return { ...prevProject, name: nextName };
    });
  };

  const handleProjectNameBlur = (nextName) => {
    const fallbackName = previousProjectNameRef.current || DEFAULT_PROJECT_NAME;
    const normalizedName = nextName?.trim() || fallbackName;

    if (!chatSessionId || !normalizedName) return;
    if (project?.name === normalizedName) return;

    if (typeof onProjectNameChange === 'function') {
      onProjectNameChange(chatSessionId, normalizedName);
    }
    setProject((previousProject) => {
      if (!previousProject) return previousProject;
      return { ...previousProject, name: normalizedName };
    });
  };

  const handleRenameProject = () => {
    const rawName = project?.name || '';
    const previousName = previousProjectNameRef.current || DEFAULT_PROJECT_NAME;
    const nextName = rawName.trim() || previousName;
    const requestId = ++renameProjectRequestIdRef.current;
    if (!chatSessionId || !nextName) return;
    if (rawName.trim() !== nextName) {
      if (typeof onProjectNameChange === 'function') {
        onProjectNameChange(chatSessionId, nextName);
      }
      setProject((previousProject) => {
        if (!previousProject) return previousProject;
        return { ...previousProject, name: nextName };
      });
    }
    if (nextName === previousName) return;
    latestRequestedProjectNameRef.current = nextName;

    renameProject({ chatSessionId: chatSessionId, name: nextName })
      .then(() => {
        if (requestId !== renameProjectRequestIdRef.current) return;
        if (latestRequestedProjectNameRef.current !== nextName) return;
        previousProjectNameRef.current = nextName;
        if (typeof onProjectNameChange === 'function') {
          onProjectNameChange(chatSessionId, nextName);
        }
        setProject((previousProject) => {
          if (!previousProject) return previousProject;
          return { ...previousProject, name: nextName };
        });
      })
      .catch((error) => {
        if (requestId !== renameProjectRequestIdRef.current) return;
        if (latestRequestedProjectNameRef.current !== nextName) return;
        console.error('Failed to rename project', error);
        if (typeof onProjectNameChange === 'function') {
          onProjectNameChange(chatSessionId, previousName);
        }
        setProject((previousProject) => {
          if (!previousProject) return previousProject;
          return { ...previousProject, name: previousName };
        });
        toast.error('Unable to rename project.');
      });
  };

  useEffect(() => {
    if (isExternalProjectNameUpdateRef.current) {
      isExternalProjectNameUpdateRef.current = false;
      return;
    }

    const currentName = project?.name?.trim() || '';
    const previousName = previousProjectNameRef.current?.trim() || '';
    if (!currentName || currentName === previousName) return;
    const timer = window.setTimeout(() => {
      handleRenameProject();
    }, PROJECT_RENAME_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [chatSessionId, project?.name]);

  const resolveRunComponentIds = useCallback(
    (flow) => {
      if (Array.isArray(flow?.topic_ids)) return flow.topic_ids;
      if (Array.isArray(flow?.topics)) return getRunComponentIds(flow.topics);
      if (Array.isArray(flowState?.topics)) return flowState.topics;
      return null;
    },
    [flowState?.topics]
  );
  if (isViewingRun) {
    return (
      <div className={styles['narratives-canvas-container']} ref={draftScrollContainerRef}>
        <div className={styles['narratives-canvas-content']}>
          <NarrativeFlowView
            activeNarrativeName={activeNarrativeName}
            flowId={flowId}
            getTopicControls={runTopicControlsBuilder}
            hidePageTitle
            isInCanvas
            key={`${flowId}-${runFlowRefreshKey}`}
            onNarrativeNameChange={handleRunNarrativeNameChange}
            onReprompt={handleRunReprompt}
            renderAfterContent={({ flow, narrativeRoute }) => (
              <>
                <RunFlowTopicsObserver
                  flow={flow}
                  onRunFlowTopicIdsChange={onRunFlowTopicIdsChange}
                  shouldShowRunTopicCard={shouldShowRunTopicCard}
                />
                {isPromptModalOpen ? (
                  <ModalBuildNarrative
                    flowId={flow?.id}
                    onNarrativeBuilt={(nextFlowId) => {
                      handleRunModalVisibilityChange(false);
                      if (typeof onFlowSelected === 'function') {
                        onFlowSelected(nextFlowId);
                      }
                    }}
                    onNarrativeCreated={({ flowId: nextFlowId, name }) => {
                      if (typeof onNarrativeCreated === 'function' && chatSessionId) {
                        onNarrativeCreated({
                          chatSessionId,
                          flowId: nextFlowId,
                          name,
                        });
                      }
                    }}
                    projectId={project?.id ?? null}
                    route={narrativeRoute}
                    selectedPrompt={selectedRunPrompt}
                    setIsModalOpen={handleRunModalVisibilityChange}
                    topicIds={
                      normalizedFlowStateComponentIds.length > 0
                        ? normalizedFlowStateComponentIds
                        : resolveRunComponentIds(flow) || []
                    }
                  />
                ) : null}
              </>
            )}
            renderProjectTitle={project ? renderProjectTitle : undefined}
            useWindowScroll={false}
          />
        </div>
        <Footer />
        {shouldShowToolbar && (
          <CanvasFloatingToolbar
            clearCanvasDisabled={isClearingCanvas || isProjectDraftLookupPending}
            clearCanvasLabel={'Create New Narrative'}
            onSelectPrompts={handleToolbarAction}
            selectPromptsDisabled={isToolbarActionDisabled}
            selectPromptsLabel={toolbarActionLabel}
            selectPromptsLoading={isToolbarActionLoading}
          />
        )}
        {preBuildModal}
        {deleteTopicCardConfirmationModal}
        {deleteDraftNarrativeConfirmationModal}
        {configureTopicModal}
      </div>
    );
  }

  return (
    <div className={styles['narratives-canvas-container']} ref={draftScrollContainerRef}>
      <div className={styles['narratives-canvas-content']}>
        {draftTopics.length > 0 && (
          <div className={styles['narratives-canvas-draft-view']}>
            {draftFlowData && (
              <>
                {project ? renderProjectTitle() : null}
                <NarrativeTitle
                  downloadData={EMPTY_DOWNLOAD_MAP}
                  flowData={draftFlowData}
                  handleReprompt={handleSelectPrompts}
                  isDraft
                  onTemplateSaved={onTemplateSaved}
                  selectedPromptsOverride={visiblePrompts}
                  setFlow={(updater) => {
                    const nextFlow =
                      typeof updater === 'function' ? updater({ name: promptModalName }) : updater;
                    const nextName = nextFlow?.name?.trim();
                    const currentName = draftNarrativeName?.trim() || DEFAULT_DRAFT_NARRATIVE_NAME;
                    const hasChangedTitle = nextName
                      ? nextName !== currentName
                      : currentName !== DEFAULT_DRAFT_NARRATIVE_NAME;

                    if (hasChangedTitle) {
                      hasUserEditedDraftNameRef.current = true;
                    }

                    if (nextName) {
                      setPromptModalName(nextName);
                    } else {
                      setPromptModalName(DEFAULT_DRAFT_NARRATIVE_NAME);
                    }
                  }}
                  titleOverride={draftNarrativeName}
                />
              </>
            )}

            <motion.div className={styles['narratives-canvas-draft-grid']} layout>
              <AnimatePresence initial={false} mode="popLayout">
                {enrichedDraftTopics.map((draft, index) => {
                  const flowIndex = flowStateTopicIds.indexOf(draft.componentId);
                  const isFlowStateDraft = flowIndex > -1;
                  const topicInManualOrder = manualTopicOrder.indexOf(draft.componentId);
                  const isManualDraft = !isFlowStateDraft && topicInManualOrder > -1;
                  const isConfigurableTopic = hasMultipleConfigVariants(draft?.configMeta);
                  const canEditTopicConfiguration =
                    isConfigurableTopic &&
                    hasAvailableAlternativeVariant({
                      config: draft?.configMeta,
                      occupiedTopicIdSet,
                      previousTopicId: draft.componentId,
                      selectedVariant: draft?.selectedVariant,
                    });
                  let topicControls = null;

                  if (isFlowStateDraft && canPersistDraftOrder) {
                    topicControls = {
                      primaryAction: isConfigurableTopic
                        ? {
                            disabled: isDraftActionPending || !canEditTopicConfiguration,
                            label: 'Edit',
                            onClick: () => handleEditConfigCard(draft),
                            tooltip:
                              !isDraftActionPending && !canEditTopicConfiguration
                                ? CONFIGURABLE_CARD_DISABLED_TOOLTIP
                                : null,
                          }
                        : {
                            disabled: isDraftActionPending,
                            label: 'Remove',
                            onClick: () => handleDraftTopicAction('remove', draft),
                          },
                      secondaryActions: [
                        ...(isConfigurableTopic
                          ? [
                              {
                                disabled: isDraftActionPending,
                                label: 'Remove',
                                onClick: () => handleDraftTopicAction('remove', draft),
                              },
                            ]
                          : []),
                        {
                          disabled:
                            isDraftActionPending || flowIndex <= 0 || flowStateTopicIds.length < 2,
                          label: 'Move Up',
                          onClick: () => handleDraftTopicAction('up', draft),
                        },
                        {
                          disabled:
                            isDraftActionPending ||
                            flowIndex === -1 ||
                            flowIndex === flowStateTopicIds.length - 1 ||
                            flowStateTopicIds.length < 2,
                          label: 'Move Down',
                          onClick: () => handleDraftTopicAction('down', draft),
                        },
                      ],
                    };
                  } else if (
                    !isFlowStateDraft &&
                    canManageManualDrafts &&
                    topicInManualOrder > -1
                  ) {
                    const manualCount = manualTopicOrder.length;
                    topicControls = {
                      primaryAction: isConfigurableTopic
                        ? {
                            disabled: !canEditTopicConfiguration,
                            label: 'Edit',
                            onClick: () => handleEditConfigCard(draft),
                            tooltip: !canEditTopicConfiguration
                              ? CONFIGURABLE_CARD_DISABLED_TOOLTIP
                              : null,
                          }
                        : {
                            disabled: false,
                            label: 'Remove',
                            onClick: () => handleManualTopicAction('remove', draft),
                          },
                      secondaryActions: [
                        ...(isConfigurableTopic
                          ? [
                              {
                                disabled: false,
                                label: 'Remove',
                                onClick: () => handleManualTopicAction('remove', draft),
                              },
                            ]
                          : []),
                        {
                          disabled: manualCount < 2 || topicInManualOrder <= 0,
                          label: 'Move Up',
                          onClick: () => handleManualTopicAction('up', draft),
                        },
                        {
                          disabled: manualCount < 2 || topicInManualOrder === manualCount - 1,
                          label: 'Move Down',
                          onClick: () => handleManualTopicAction('down', draft),
                        },
                      ],
                    };
                  }
                  return (
                    <motion.div
                      className={styles['narratives-canvas-draft-card']}
                      data-testid={draft.componentId}
                      key={`draft-topic-${draft.componentId}`}
                      layout
                      layoutId={`draft-topic-${draft.componentId}`}
                      {...TOPIC_CARD_MOTION_PROPS}
                    >
                      <div ref={isManualDraft ? registerManualCardRef(draft.componentId) : null}>
                        <NarrativeTopicCard
                          createdDatetime={draftFlowData?.createdDatetime}
                          initialTopicData={buildDraftTopicData(draft)}
                          isDraftCard
                          isInCanvas
                          keyIndex={index}
                          onPreview={() => {}}
                          setDownloadData={() => {}}
                          topic={buildTopicShell(draft)}
                          topicControls={topicControls}
                          useWindowScroll={false}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          </div>
        )}
      </div>

      <Footer />
      {shouldShowToolbar && (
        <CanvasFloatingToolbar
          clearCanvasDisabled={isClearingCanvas}
          clearCanvasLabel={'Clear Canvas'}
          onSelectPrompts={handleToolbarAction}
          selectPromptsDisabled={isToolbarActionDisabled}
          selectPromptsLabel={toolbarActionLabel}
          selectPromptsLoading={isToolbarActionLoading}
        />
      )}
      {preBuildModal}
      {deleteTopicCardConfirmationModal}
      {deleteDraftNarrativeConfirmationModal}
      {configureTopicModal}
    </div>
  );
};

NarrativesCanvas.propTypes = {
  activeNarrativeName: PropTypes.string,
  activeTopicConfiguration: PropTypes.shape({
    config: PropTypes.shape({
      grouping: PropTypes.string,
      signatureDimensions: PropTypes.arrayOf(PropTypes.string),
      totalVariants: PropTypes.number,
      variantDimensions: PropTypes.arrayOf(PropTypes.string),
      variantMap: PropTypes.object,
      variantOptions: PropTypes.object,
    }),
    id: PropTypes.string,
    mode: PropTypes.oneOf(['draft', 'run']),
    name: PropTypes.string,
    occupiedTopicIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    previousTopicId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    selectedVariant: PropTypes.object,
    vizType: PropTypes.string,
  }),
  chatSessionId: PropTypes.string,
  externalIsFavorite: PropTypes.bool,
  externalProjectName: PropTypes.string,
  flowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flowState: PropTypes.shape({
    defaultPromptAnswers: PropTypes.object,
    editablePromptAnswers: PropTypes.object,
    flowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    focusFlowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    narrativeId: PropTypes.string,
    narrativeName: PropTypes.string,
    promptRequirements: PropTypes.array,
    readOnlyPromptAnswers: PropTypes.object,
    sameLevelPrompts: PropTypes.array,
    searchPrompts: PropTypes.array,
    topics: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  }),
  hasProjectDraft: PropTypes.bool,
  isProjectDraftLookupPending: PropTypes.bool,
  isRunTopicMutationInProgress: PropTypes.bool,
  onActiveTopicConfigurationChange: PropTypes.func,
  onDraftCleared: PropTypes.func,
  onDraftConsumed: PropTypes.func,
  onDraftSaved: PropTypes.func,
  onEnsureChatSession: PropTypes.func,
  onFavoriteChange: PropTypes.func,
  onFlowSelected: PropTypes.func,
  onFlowStateChange: PropTypes.func,
  onInsertRunTopicConfiguration: PropTypes.func,
  onNarrativeCreated: PropTypes.func,
  onNarrativeDeleted: PropTypes.func,
  onNarrativeNameChange: PropTypes.func,
  onProjectNameChange: PropTypes.func,
  onProjectNotFound: PropTypes.func,
  onReplaceRunTopicConfiguration: PropTypes.func,
  onRequestTopicConfigurationLookup: PropTypes.func,
  onRunFlowTopicIdsChange: PropTypes.func,
  onSelectedTopicsChange: PropTypes.func,
  onTemplateSaved: PropTypes.func,
  runFlowRefreshKey: PropTypes.number,
  selectedTopicCards: PropTypes.arrayOf(
    PropTypes.shape({
      configMeta: PropTypes.shape({
        grouping: PropTypes.string,
        signatureDimensions: PropTypes.arrayOf(PropTypes.string),
        totalVariants: PropTypes.number,
        variantDimensions: PropTypes.arrayOf(PropTypes.string),
        variantMap: PropTypes.object,
        variantOptions: PropTypes.object,
      }),
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string,
      selectedVariant: PropTypes.object,
      variantSignature: PropTypes.string,
      vizType: PropTypes.string,
    })
  ),
  topicConfigurationLookup: PropTypes.object,
};

export default NarrativesCanvas;
